import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
  useMemo,
} from 'react'
import { flushSync } from 'react-dom'

import { XStack, Input, View } from 'tamagui'
import { msg } from '@lingui/core/macro'
import type { MessageDescriptor } from '@lingui/core'
import { useLingui } from '@lingui/react/macro'
import type { TextInput as RNTextInput } from 'react-native'

import { Text } from '../Text'
import { Button } from '../Button/Button'
import { Icon } from '../Icon'
import { useDynamicIslandActions } from '../DynamicIsland/useDynamicIsland'

const SearchOverlay = React.lazy(() =>
  import('./SearchOverlay').then((m) => ({ default: m.SearchOverlay }))
)

const SEARCH_ROW_ID = 'search-overlay'

// --- Placeholder typewriter animation ---

const placeholderSuggestions: MessageDescriptor[] = [
  msg`React events near me`,
  msg`AI presentations`,
  msg`Guilds in London`,
  msg`Events this week`,
  msg`JavaScript meetups`,
  msg`Civic tech talks`,
  msg`Events about testing`,
  msg`GraphQL presentations`,
  msg`Guilds for React developers`,
  msg`Events happening tomorrow`,
  msg`Web performance talks`,
  msg`Events in Toronto`,
  msg`Career growth presentations`,
  msg`Machine learning Guilds`,
  msg`React Native in London`,
  msg`Design systems events`,
  msg`Start typing, or just hit Enter`,
  msg`Events nearby, new Guilds, your next talk`,
  msg`Type a vibe, find your people`,
  msg`Search anything or create something`,
  msg`There's always something happening`,
  msg`Your next favourite event is one search away`,
  msg`Find your people`,
]

const PLACEHOLDER_TICK_MS = 45
const PAUSE_AFTER_TYPING_MS = 2500
const PAUSE_AFTER_ERASING_MS = 500

const usePlaceholderTypewriter = (suggestions: string[]) => {
  const [displayedText, setDisplayedText] = useState('')

  const stateRef = useRef({
    index: Math.floor(Math.random() * suggestions.length),
    charIndex: 0,
    phase: 'typing' as 'typing' | 'paused' | 'erasing',
    pauseUntil: 0,
  })

  useEffect(() => {
    if (suggestions.length === 0) {
      setDisplayedText('')
      return
    }

    const s = stateRef.current
    s.charIndex = 0
    s.phase = 'typing'

    const tick = () => {
      const now = Date.now()
      if (s.pauseUntil > now) return

      const target = suggestions[s.index]

      switch (s.phase) {
        case 'typing':
          if (s.charIndex < target.length) {
            s.charIndex++
            setDisplayedText(target.slice(0, s.charIndex))
          } else {
            s.phase = 'paused'
            s.pauseUntil = now + PAUSE_AFTER_TYPING_MS
          }
          break

        case 'paused':
          s.phase = 'erasing'
          break

        case 'erasing':
          if (s.charIndex > 0) {
            s.charIndex--
            setDisplayedText(target.slice(0, s.charIndex))
          } else {
            let next = Math.floor(Math.random() * (suggestions.length - 1))
            if (next >= s.index) next++
            s.index = next
            s.phase = 'typing'
            s.pauseUntil = now + PAUSE_AFTER_ERASING_MS
          }
          break
      }
    }

    const interval = setInterval(tick, PLACEHOLDER_TICK_MS)
    return () => clearInterval(interval)
  }, [suggestions])

  // Blinking cursor
  const [cursorVisible, setCursorVisible] = useState(true)
  useEffect(() => {
    const timer = setInterval(() => setCursorVisible((v) => !v), 530)
    return () => clearInterval(timer)
  }, [])

  return { text: displayedText, cursor: cursorVisible ? '|' : '\u00A0' }
}

// Measure text width using an off-screen canvas with the input's actual font.
let _measureCanvas: HTMLCanvasElement | null = null
const measureTextWidth = (text: string, font: string): number => {
  if (typeof document === 'undefined') return 0
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas')
  const ctx = _measureCanvas.getContext('2d')
  if (!ctx) return 0
  ctx.font = font
  return Math.ceil(ctx.measureText(text).width)
}

type SearchInputElement = RNTextInput

const SearchInput = ({
  inputRef,
  onValueChange,
  onEscape,
  setSearchTextRef,
}: {
  inputRef: React.RefObject<SearchInputElement>
  onValueChange: (value: string) => void
  onEscape: () => void
  setSearchTextRef: React.MutableRefObject<((text: string) => void) | null>
}) => {
  const { t } = useLingui()
  const suggestions = useMemo(
    () => placeholderSuggestions.map((s) => t(s)),
    [t]
  )
  const [hasTyped, setHasTyped] = useState(false)
  const { text: placeholderText, cursor: placeholderCursor } =
    usePlaceholderTypewriter(hasTyped ? [] : suggestions)
  const showPlaceholder = !hasTyped

  const [value, setValue] = useState('')

  // Register a setter so the parent can programmatically update the input value
  useEffect(() => {
    setSearchTextRef.current = (text: string) => {
      setValue(text)
      onValueChange(text)
    }
    return () => {
      setSearchTextRef.current = null
    }
  }, [onValueChange, setSearchTextRef])
  const [inputMinWidth, setInputMinWidth] = useState<number | undefined>(
    undefined
  )
  const computedFontRef = useRef('')
  const naturalWidthRef = useRef(0)

  // Capture the input's actual computed font and natural width after mount
  useEffect(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current as unknown as HTMLElement | null
      if (!el) return
      const cs = window.getComputedStyle(el)
      computedFontRef.current = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
      naturalWidthRef.current = el.clientWidth
    })
  }, [inputRef])

  const handleChangeText = useCallback(
    (text: string) => {
      if (text) setHasTyped(true)
      setValue(text)
      onValueChange(text)
    },
    [onValueChange]
  )

  // Measure text (typed value or animated placeholder) using the input's real font.
  // Only expand beyond the natural flex width — never contract below it.
  const displayText = value || placeholderText
  useLayoutEffect(() => {
    if (!displayText || !computedFontRef.current) {
      setInputMinWidth(undefined)
      return
    }
    const textWidth = measureTextWidth(displayText, computedFontRef.current)
    const needed = textWidth + 12 // cursor + breathing room
    if (needed > naturalWidthRef.current) {
      const maxWidth =
        typeof window !== 'undefined'
          ? Math.min(560, window.innerWidth - 148)
          : 400
      setInputMinWidth(Math.min(needed, maxWidth))
    } else {
      setInputMinWidth(undefined)
    }
  }, [displayText])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault()
          onEscape()
          break
        case 'ArrowDown':
        case 'ArrowUp':
        case 'Enter':
          // Let these propagate to the global listener in SearchOverlay
          // but prevent the input's default behavior (cursor movement, form submit)
          e.preventDefault()
          break
      }
    },
    [onEscape]
  )
  const hiddenCaretProps =
    showPlaceholder && !value
      ? ({
          style: {
            caretColor: 'transparent',
          } satisfies React.CSSProperties,
        } as unknown as Partial<React.ComponentProps<typeof Input>>)
      : {}

  return (
    <XStack flex={1} alignItems='center' gap='$2' paddingHorizontal='$2'>
      <Icon name='prompt' color='$colorMuted' size='$5' />
      <View flex={1} position='relative'>
        {showPlaceholder && !value && (
          <XStack
            position='absolute'
            top={0}
            left={0}
            right={0}
            bottom={0}
            alignItems='center'
            overflow='hidden'
            style={{ pointerEvents: 'none' }}
          >
            <Text
              fontSize='$5'
              color='$color'
              style={{ opacity: 0.35, whiteSpace: 'nowrap' }}
              numberOfLines={1}
            >
              {placeholderText}
            </Text>
            <Text fontSize='$5' color='$color'>
              {placeholderCursor}
            </Text>
          </XStack>
        )}
        <Input
          ref={inputRef}
          flex={1}
          value={value}
          onChangeText={handleChangeText}
          placeholder=''
          autoComplete='off'
          borderWidth={0}
          {...hiddenCaretProps}
          outlineWidth={0}
          borderRadius={0}
          backgroundColor='transparent'
          fontSize='$5'
          height='$8'
          padding='$0'
          focusStyle={{ borderWidth: 0, outlineWidth: 0 }}
          hoverStyle={{ borderWidth: 0, outlineWidth: 0 }}
          {...(inputMinWidth ? { minWidth: inputMinWidth } : {})}
          {...{ onKeyDown: handleKeyDown }}
        />
      </View>
    </XStack>
  )
}

export const SearchProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<SearchInputElement>(null)
  const setSearchTextRef = useRef<((text: string) => void) | null>(null)
  const { pushRows, popRows } = useDynamicIslandActions()

  // Stable refs for callbacks used inside pushed rows
  const onValueChangeRef = useRef(setInputValue)
  const closeSearchRef = useRef<() => void>(() => undefined)

  const populateSearch = useCallback((text: string) => {
    setInputValue(text)
    setSearchTextRef.current?.(text)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  const closeSearch = useCallback(() => {
    setIsOpen(false)
    setInputValue('')
    popRows(SEARCH_ROW_ID)
  }, [popRows])

  closeSearchRef.current = closeSearch

  const openSearch = useCallback(() => {
    // flushSync forces the SearchInput to mount synchronously so we can focus
    // it inside the same user-gesture tick. iOS Safari only opens the on-screen
    // keyboard when focus() runs synchronously within a tap/click handler —
    // deferring via useEffect/requestAnimationFrame loses user activation.
    flushSync(() => {
      setIsOpen(true)
      setInputValue('')
      pushRows(
        [
          [
            {
              id: 'search-input',
              component: (
                <SearchInput
                  inputRef={inputRef}
                  onValueChange={(v) => onValueChangeRef.current(v)}
                  onEscape={() => closeSearchRef.current()}
                  setSearchTextRef={setSearchTextRef}
                />
              ),
              containerStyle: { flex: 1 },
            },
            {
              id: 'search-close',
              component: (
                <Button
                  minHeight='$8'
                  height='$8'
                  width='$8'
                  padding='$0'
                  alignItems='center'
                  justifyContent='center'
                  secondary
                  onPress={() => closeSearchRef.current()}
                >
                  <Icon
                    name='add'
                    color='$color'
                    transform={[{ rotateZ: '45deg' }]}
                  />
                </Button>
              ),
            },
          ],
        ],
        SEARCH_ROW_ID
      )
    })
    inputRef.current?.focus()
  }, [pushRows])

  // Global keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (isOpen) {
          closeSearch()
        } else {
          openSearch()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, openSearch, closeSearch])

  // Listen for custom event from search icon in Dynamic Island
  useEffect(() => {
    const handler = () => openSearch()
    window.addEventListener('guild-open-search', handler)
    return () => window.removeEventListener('guild-open-search', handler)
  }, [openSearch])

  return (
    <>
      {children}
      {isOpen && (
        <React.Suspense fallback={null}>
          <SearchOverlay
            inputValue={inputValue}
            onClose={closeSearch}
            onPopulateSearch={populateSearch}
          />
        </React.Suspense>
      )}
    </>
  )
}
